/**
 * @name Layout.js
 */
import React, { useState, useEffect, useContext } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { AuthContext } from '@context';
import { ROLES } from '@constants/roles';
import Header from './Header';
import Footer from './Footer';
import Sidebar from './Sidebar';
import MobileMenu from './MobileMenu';
import PermissionErrorBoundary from './PermissionErrorBoundary';

const Layout = ({ children, noFooter }) => {
  const location = useLocation();
  const { auth } = useContext(AuthContext);

  const [activeMenu, setActiveMenu] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (!mobile) {
        setIsMobileMenuOpen(false);
      } else {
        setIsSidebarOpen(false);
        setActiveMenu(null);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const openSidebar = (menuId) => {
    if (activeMenu === menuId) {
      setIsSidebarOpen(!isSidebarOpen);
      if (isSidebarOpen) setActiveMenu(null);
    } else {
      setActiveMenu(menuId);
      setIsSidebarOpen(true);
      setIsMobileMenuOpen(false);
      window.dispatchEvent(new CustomEvent('layout-sidebar-open'));
    }
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
    setActiveMenu(null);
  };

  // 페이지 이동 시 오버레이/사이드바 자동 닫기
  useEffect(() => {
    closeSidebar();
  }, [location.pathname]);

  return (
    <div className="flex flex-col h-screen bg-gray-50 font-sans text-gray-800 overflow-hidden relative">

      {/* 🚨 핵심 수정: Header에 isSidebarOpen 속성을 전달해야 서브메뉴(드롭다운)가 열립니다! */}
      <Header
        activeMenu={activeMenu}
        onMenuSelect={openSidebar}
        toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
        toggleMobileMenu={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        isMobile={isMobile}
        isSidebarOpen={isSidebarOpen}
      />

      <MobileMenu
        isMobileMenuOpen={isMobileMenuOpen}
        toggleMobileMenu={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      />

      <div className="flex flex-1 overflow-hidden relative z-40">

        {/* 모바일 화면일 때만 나타나는 Sidebar */}
        <Sidebar
          activeMenu={activeMenu}
          onMenuSelect={openSidebar}
          isSidebarOpen={isSidebarOpen}
          closeSidebar={closeSidebar}
          isMobile={isMobile}
        />

        <div className={`flex-1 ${noFooter ? 'overflow-hidden' : 'overflow-auto'} bg-gray-50 flex flex-col min-w-0 relative w-full z-0`}>


          <main className={`flex-1 w-full ${noFooter ? 'flex flex-col min-h-0' : ''}`}>
            <PermissionErrorBoundary resetKey={location.pathname}>
              {children}
            </PermissionErrorBoundary>
          </main>

          {!noFooter && <Footer />}
        </div>
      </div>
    </div>
  );
};

export default Layout;